/**
 * Match this handle (README §5). Halo candidates, each with a reason it was
 * offered ("number on contact" / "shared line"), plus the Matched-ceiling
 * rule stated plainly (idea #2). Every candidate offers two actions:
 * "Match" (writes the mapping back to Halo — the default, §3 "Storage") and
 * "Keep as alias only" (the pressure-relief valve for a messy handle that
 * should not pollute a canonical contact record — a shared line, a temp
 * number, a spouse's phone — writeBackToHalo: false, local alias table
 * only).
 *
 * Both call the single `resolve-identity` operation with `action: 'match'`;
 * they differ only in `writeBackToHalo`. On success the thread comes back
 * Matched (services/ops's `setTier` is the only writer of `thread.tier`) and
 * is pushed into the store.
 */
import { useEffect, useState } from 'react'
import type { ThreadSummary } from '@communicator/shared'
import { Button, Chip, Modal } from '../ui'
import { ops, OpsError } from '../../lib/ops'
import { useStore } from '../../store'
import {
  modalReads,
  MATCH_CANDIDATES_SEAM,
  type ContactSearchResult,
  type MatchCandidate,
  type MatchCandidatesResult,
} from './reads'
import {
  GENERIC_OP_ERROR,
  MATCH_CEILING_TEXT,
  NO_CANDIDATES_TEXT,
  REASON_LABEL,
  REASON_TONE,
  unwiredText,
} from './format'

export function MatchModal() {
  const modal = useStore((s) => s.modal)
  const activeId = useStore((s) => s.activeId)
  const closeModal = useStore((s) => s.closeModal)
  const upsertThread = useStore((s) => s.upsertThread)

  const open = modal === 'match'

  const [result, setResult] = useState<MatchCandidatesResult>(MATCH_CANDIDATES_SEAM)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null) // contactId currently submitting
  /**
   * "This number is always this person", which is NOT what matching a call
   * means. Off by default: a client's main line belongs to everyone at that
   * company, and binding it to whoever rang first would mislabel every later
   * call from them.
   */
  const [rememberHandle, setRememberHandle] = useState(false)
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState<ContactSearchResult>({ wired: true, results: [], tooShort: true })
  const [searching, setSearching] = useState(false)

  // Debounced, because this reaches Halo on every keystroke otherwise. 300ms
  // is long enough to finish a word and short enough not to feel laggy.
  useEffect(() => {
    if (!open) return
    const q = query.trim()
    if (q.length < 2) {
      setSearch({ wired: true, results: [], tooShort: true })
      setSearching(false)
      return
    }
    setSearching(true)
    let cancelled = false
    const timer = setTimeout(() => {
      modalReads
        .searchContacts(q)
        .then((r) => {
          if (!cancelled) setSearch(r)
        })
        .catch(() => {
          if (!cancelled) {
            setSearch({ wired: false, results: [], reason: 'The contact search could not be run.' })
          }
        })
        .finally(() => {
          if (!cancelled) setSearching(false)
        })
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [open, query])

  // A fresh modal starts with a fresh search, or reopening it shows the last
  // person somebody looked for on a different conversation.
  useEffect(() => {
    if (!open) {
      setQuery('')
      setSearch({ wired: true, results: [], tooShort: true })
      setRememberHandle(false)
    }
  }, [open])

  useEffect(() => {
    if (!open || !activeId) return
    let cancelled = false
    modalReads.matchCandidates(activeId).then((r) => {
      if (!cancelled) setResult(r)
    })
    return () => {
      cancelled = true
    }
  }, [open, activeId])

  useEffect(() => {
    if (!open) {
      setError(null)
      setBusy(null)
      setResult(MATCH_CANDIDATES_SEAM)
    }
  }, [open])

  if (!open) return null

  async function resolve(
    candidate: { contactId: string; name: string },
    writeBackToHalo: boolean,
  ) {
    if (!activeId) return
    setBusy(candidate.contactId)
    setError(null)
    try {
      const result = (await ops.call('resolve-identity', {
        threadId: activeId,
        action: 'match',
        contactId: candidate.contactId,
        writeBackToHalo,
        // Matching this conversation is NOT the same as claiming the number.
        // A shared main line belongs to everyone at that company, so this is
        // an explicit, separate choice — and it defaults to off.
        aliasHandle: rememberHandle,
        name: candidate.name,
      })) as { thread: ThreadSummary }
      upsertThread(result.thread)
      closeModal()
    } catch (e) {
      setError(e instanceof OpsError ? e.message : GENERIC_OP_ERROR)
    } finally {
      setBusy(null)
    }
  }

  return (
    <Modal open={open} title="Match this handle" onClose={closeModal}>
      <p data-testid="match-modal-ceiling-copy" className="mb-3 text-[13px] text-mid">
        {MATCH_CEILING_TEXT}
      </p>

      {error && (
        <p
          data-testid="match-modal-error"
          role="alert"
          className="mb-3 rounded-[8px] bg-bad-subtle px-3 py-2 text-[12px] text-bad"
        >
          {error}
        </p>
      )}

      {!result.wired ? (
        <p data-testid="match-modal-unreachable" className="text-[13px] text-warn">
          {unwiredText('candidates', result.reason)}
        </p>
      ) : result.candidates.length === 0 ? (
        <p data-testid="match-modal-empty" className="text-[13px] text-lo">
          {NO_CANDIDATES_TEXT}
        </p>
      ) : (
        <div data-testid="match-modal-candidates">

          {result.candidates.map((c) => (
            <div
              key={c.contactId}
              data-testid={`match-modal-candidate-${c.contactId}`}
              className="mb-2 rounded-[8px] border border-subtle bg-card p-2.5"
            >
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <span className="text-[13px] font-medium text-hi">
                  {c.name}
                  {c.company ? ` · ${c.company}` : ''}
                </span>
                <Chip tone={REASON_TONE[c.reason]}>{REASON_LABEL[c.reason]}</Chip>
              </div>
              <div className="flex gap-2">
                <Button
                  data-testid={`match-modal-candidate-${c.contactId}-match-btn`}
                  disabled={busy !== null}
                  onClick={() => resolve(c, true)}
                >
                  This caller
                </Button>
                <Button
                  variant="secondary"
                  data-testid={`match-modal-candidate-${c.contactId}-alias-btn`}
                  disabled={busy !== null}
                  onClick={() => resolve(c, false)}
                >
                  This caller, don't touch Halo
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

        {/* The distinction Andrew asked for, stated where the decision is
            made. Matching answers "who called"; the alias answers "who is
            this number, always" — and for a company main line those have
            different answers. Off by default, and the warning is shown
            rather than assumed, because the person clicking may not know
            whether the number they are looking at is shared. */}
        {/* Fix round 2 (finding 12): was `bg-elevated` inside a `bg-elevated`
            Modal (Modal.tsx:32) — invisible in every theme. */}
        <label className="mb-2.5 flex items-start gap-2 rounded-[8px] bg-sunken px-2.5 py-2 text-[12px] text-mid">
          <input
            type="checkbox"
            data-testid="match-modal-remember-handle"
            className="mt-0.5"
            checked={rememberHandle}
            onChange={(e) => setRememberHandle(e.target.checked)}
          />
          <span>
            <span className="font-medium text-hi">Remember this number as them from now on.</span>{' '}
            Leave off for a company main line — it belongs to everyone there, and later callers
            would be labelled as this person. Matching below applies to this conversation either way.
          </span>
        </label>

      {/* SEARCH — the answer to "it was none of those".
          A shared main line returns everyone at the company, and the caller is
          one of them; the candidate list cannot say which. Typing a name is
          how a dispatcher records who actually rang, and it is also the only
          route when the number matched nobody at all. */}
      <div className="mt-3 border-t border-subtle pt-3">
        <label htmlFor="match-search" className="mb-1.5 block text-[12px] font-medium text-hi">
          Someone else?
        </label>
        <input
          id="match-search"
          data-testid="match-modal-search"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search contacts by name or email…"
          className="w-full rounded-[8px] border border-subtle bg-card px-2.5 py-1.5 text-[13px] text-hi placeholder:text-lo"
        />

        {searching ? (
          <p className="mt-1.5 text-[12px] text-lo">Searching…</p>
        ) : !search.wired ? (
          <p role="alert" data-testid="match-modal-search-error" className="mt-1.5 text-[12px] text-warn">
            {search.reason}
          </p>
        ) : search.tooShort ? null : search.results.length === 0 ? (
          <p data-testid="match-modal-search-empty" className="mt-1.5 text-[12px] text-lo">
            No contact matches “{query.trim()}”. Halo answered — this is a real finding, not a
            failure to look.
          </p>
        ) : (
          <div data-testid="match-modal-search-results" className="mt-1.5">
            {search.results.map((r) => (
              <div
                key={r.contactId}
                data-testid={`match-modal-search-result-${r.contactId}`}
                className="mb-1.5 flex items-center justify-between gap-2 rounded-[8px] border border-subtle bg-card px-2.5 py-2"
              >
                <span className="text-[13px] text-hi">
                  {r.name}
                  {r.company ? <span className="text-mid"> · {r.company}</span> : null}
                </span>
                <Button
                  data-testid={`match-modal-search-result-${r.contactId}-btn`}
                  disabled={busy !== null}
                  onClick={() => resolve({ contactId: r.contactId, name: r.name }, true)}
                >
                  This caller
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  )
}
